/**
 * EsignError — typed error surface for @metabus/esign-client.
 *
 * Mirrors @metabus/dialer-client's DialerError shape (same discriminated
 * `kind` pattern) so callers already familiar with that package's error
 * handling get the same shape here.
 *
 *   - http_error    — dialer returned a non-2xx the other kinds don't cover.
 *                     status + body excerpt populated.
 *   - not_found     — dialer returned 404 for a tracking_id it never
 *                     created (the contract doc: "never proxies an
 *                     arbitrary Documenso document id the dialer never
 *                     created").
 *   - network_error — fetch() threw (DNS, TLS, ECONNREFUSED, abort). cause set.
 *   - parse_error   — dialer returned 2xx but the body was not valid JSON.
 */
export type EsignErrorKind = 'http_error' | 'not_found' | 'network_error' | 'parse_error';

export interface EsignErrorOpts {
  kind: EsignErrorKind;
  message: string;
  status?: number;
  detail?: string;
  cause?: unknown;
}

export class EsignError extends Error {
  public readonly kind: EsignErrorKind;
  public readonly status?: number;
  public readonly detail?: string;
  // Deliberately NOT `override`: this package is consumed as raw TS source
  // by apps whose own tsconfig `lib` may predate ES2022 (e.g. apps/operator
  // targets ES2020), where the base `Error` class has no `cause` member to
  // override. Declaring it plain keeps this package portable across every
  // consumer's lib setting instead of only the ones matching this repo's.
  public readonly cause?: unknown;

  constructor(opts: EsignErrorOpts) {
    super(opts.message);
    this.name = 'EsignError';
    this.kind = opts.kind;
    if (opts.status !== undefined) this.status = opts.status;
    if (opts.detail !== undefined) this.detail = opts.detail;
    if (opts.cause !== undefined) this.cause = opts.cause;
  }
}

/** Truncate an arbitrary string to a defensive body excerpt. */
export function bodyExcerpt(s: string, max = 500): string {
  if (s.length <= max) return s;
  return s.slice(0, max) + `… [truncated ${s.length - max} chars]`;
}
